import React, { useMemo, useState } from "react";
import { Box, Paper, Typography, Stack, Button, Alert } from "@mui/material";

export default function ReferAndEarn({ title = "Refer & Earn", onlyConsumer = false, sponsorUsername = "" }) {
  const [msg, setMsg] = useState("");

  const user = useMemo(() => {
    try {
      // Prefer namespaced storage for the current section (user/agency/employee/business)
      const p = (typeof window !== "undefined" && window.location && window.location.pathname) || "";
      const ns = p.startsWith("/agency") ? "agency" : p.startsWith("/employee") ? "employee" : p.startsWith("/business") ? "business" : "user";
      const primary =
        localStorage.getItem(`user_${ns}`) ||
        sessionStorage.getItem(`user_${ns}`);
      // Fallbacks: legacy caches
      const fallback =
        (ns !== "user" ? (localStorage.getItem("user_user") || sessionStorage.getItem("user_user")) : null) ||
        localStorage.getItem("user") ||
        sessionStorage.getItem("user") ||
        localStorage.getItem("user_employee") ||
        sessionStorage.getItem("user_employee");
      const ls = primary || fallback;
      const parsed = ls ? JSON.parse(ls) : {};
      // Support nested shapes like { user: { ... } }
      return parsed && typeof parsed === "object" && parsed.user && typeof parsed.user === "object" ? parsed.user : parsed;
    } catch {
      return {};
    }
  }, []);

  const sponsorId = useMemo(() => {
    try {
      // 1) If a sponsorUsername was explicitly provided (e.g., Employee dashboard), always use it
      if (sponsorUsername) return String(sponsorUsername).trim();

      // 2) Consumer-only view: use logged-in username only (source of truth)
      if (onlyConsumer) {
        const uname = user?.username || (user && user.user && user.user.username) || "";
        return String(uname).trim();
      }

      // 3) Else (employee/agency contexts without explicit sponsorUsername): prefer sponsor fields
      const src =
        user?.sponsor_id ||
        user?.prefixed_id ||
        (user && user.user && (user.user.sponsor_id || user.user.prefixed_id)) ||
        user?.username ||
        (user && user.user && user.user.username) ||
        "";
      return String(src).trim();
    } catch {
      return "";
    }
  }, [sponsorUsername, onlyConsumer, user]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  // Determine current user's role/category to decide which links to show
  const appRole = useMemo(() => {
    const r =
      (localStorage.getItem("role_employee") ||
        sessionStorage.getItem("role_employee") ||
        localStorage.getItem("role_agency") ||
        sessionStorage.getItem("role_agency") ||
        localStorage.getItem("role_user") ||
        sessionStorage.getItem("role_user") ||
        localStorage.getItem("role") ||
        sessionStorage.getItem("role") ||
        user?.role ||
        user?.role_name ||
        (user && user.user && (user.user.role || user.user.role_name)) ||
        "")
        .toString()
        .toLowerCase();
    return r;
  }, [user]);

  const userCategory = useMemo(() => {
    const c = (user?.category || user?.agency_category || user?.agency_level || "")
      .toString()
      .toLowerCase();
    return c;
  }, [user]);

  const isEmployee = appRole === "employee";
  const isAgency = appRole === "agency";
  const isSubFranchise =
    appRole === "agency" && (userCategory === "agency_sub_franchise" || userCategory === "sub_franchise");

  // Additional role flags
  const isMerchant = appRole === "merchant" || appRole === "business";
  const isConsumerRole = appRole === "consumer" || appRole === "user";

  // Restricted agency hierarchy (no refer links)
  const isRestrictedAgency =
    isAgency &&
    ["pincode", "pincode_coordinator", "district", "district_coordinator", "state", "state_coordinator"].includes(
      userCategory
    );

  // Visibility flags based on finalized rules:
  // - consumer: Consumer only
  // - merchant: Consumer only
  // - employee: Merchant only
  // - agency (sub_franchise): Sub-franchise + Merchant
  // - agency (pincode/district/state + coordinators): no links
  const allowedTypes = useMemo(() => {
    if (onlyConsumer) return new Set(["consumer"]);
    if (isMerchant) return new Set(["consumer"]);
    if (isEmployee) return new Set(["merchant"]);
    if (isAgency) {
      if (isSubFranchise) return new Set(["sub_franchise", "merchant"]);
      if (isRestrictedAgency) return new Set([]);
      return new Set([]); // default for other agency types: no links
    }
    if (isConsumerRole) return new Set(["consumer"]);
    return new Set([]);
  }, [onlyConsumer, isMerchant, isEmployee, isAgency, isSubFranchise, isRestrictedAgency, isConsumerRole]);

  const showEmployeeLink = allowedTypes.has("employee"); // intentionally false for all roles per spec
  const showConsumerLink = allowedTypes.has("consumer");
  const showSubFranchiseLink = allowedTypes.has("sub_franchise");
  const showMerchantLink = allowedTypes.has("merchant");

  const getLink = (role, extra = {}) => {
    const params = new URLSearchParams({
      ...(sponsorId ? { sponsor: sponsorId } : {}),
      ...extra,
    });
    const qs = params.toString();
    return `${origin}/auth/register-v2/${role}${qs ? `?${qs}` : ""}`;
  };

  const links = useMemo(() => {
    // 1) Consumer registration link
    const consumer = getLink("user");
    // 2) Employee registration link
    const employee = getLink("employee");
    // 3) Sub-Franchise Agency registration link
    const subFranchise = getLink("agency", { agency_level: "sub_franchise" });
    // 4) Merchant (Business) registration link
    const merchant = getLink("business");
    return { consumer, employee, subFranchise, merchant };
  }, [sponsorId, origin]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setMsg("Sharable link copied to clipboard.");
      setTimeout(() => setMsg(""), 2500);
    } catch {
      setMsg("Copy failed. Select and copy the link manually.");
      setTimeout(() => setMsg(""), 3000);
    }
  };

  const buildShareText = (label) => {
    const idText = sponsorId ? `Sponsor ID: ${sponsorId}\n` : "";
    return `Join me on Trikonekt.\n${idText}Use this ${label} registration link:`;
  };

  const openNew = (href) => {
    try {
      window.open(href, "_blank", "noopener,noreferrer");
    } catch (e) {}
  };

  const getWhatsAppUrl = (link, text) =>
    `https://wa.me/?text=${encodeURIComponent(`${text} ${link}`)}`;

  const getTelegramUrl = (link, text) =>
    `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;

  const shareNative = async (link, text) => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Refer & Earn", text, url: link });
        setMsg("Share dialog opened.");
        setTimeout(() => setMsg(""), 1500);
      } catch (e) {}
    } else {
      openNew(getWhatsAppUrl(link, text));
    }
  };


  return (
    <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: "#fff" }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#0C2D48", mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
        Share these links. New registrations will auto-fill your Sponsor ID.
      </Typography>

      {msg ? (
        <Alert severity="success" sx={{ mb: 1 }}>
          {msg}
        </Alert>
      ) : null}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ flexWrap: "wrap" }}>
        {showConsumerLink && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              sx={{ textTransform: "none" }}
              onClick={() => shareNative(links.consumer, buildShareText("Consumer"))}
            >
              Share Consumer
            </Button>
            {/* <Button
              variant="outlined"
              sx={{ textTransform: "none" }}
              onClick={() => openNew(getWhatsAppUrl(links.consumer, buildShareText("Consumer")))}
            >
              WhatsApp
            </Button>
            <Button
              variant="outlined"
              sx={{ textTransform: "none" }}
              onClick={() => openNew(getTelegramUrl(links.consumer, buildShareText("Consumer")))}
            >
              Telegram
            </Button> */}
            {/* <Button
              variant="contained"
              sx={{ textTransform: "none" }}
              onClick={() => copy(links.consumer)}
            >
              Copy
            </Button> */}
          </Stack>
        )}

        {showEmployeeLink && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              sx={{ textTransform: "none" }}
              onClick={() =>
                sponsorId
                  ? shareNative(links.employee, buildShareText("Employee"))
                  : setMsg("Sponsor ID missing. Please re-login.")
              }
            >
              Share Employee
            </Button>
            {/* <Button
              variant="outlined"
              sx={{ textTransform: "none" }}
              onClick={() =>
                sponsorId
                  ? openNew(getWhatsAppUrl(links.employee, buildShareText("Employee")))
                  : setMsg("Sponsor ID missing. Please re-login.")
              }
            >
              WhatsApp
            </Button>
            <Button
              variant="outlined"
              sx={{ textTransform: "none" }}
              onClick={() =>
                sponsorId
                  ? openNew(getTelegramUrl(links.employee, buildShareText("Employee")))
                  : setMsg("Sponsor ID missing. Please re-login.")
              }
            >
              Telegram
            </Button>
            <Button
              variant="contained"
              sx={{ textTransform: "none" }}
              onClick={() =>
                sponsorId ? copy(links.employee) : setMsg("Sponsor ID missing. Please re-login.")
              }
            >
              Copy Employee
            </Button> */}
          </Stack>
        )}

        {showSubFranchiseLink && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              sx={{ textTransform: "none" }}
              onClick={() =>
                sponsorId
                  ? shareNative(links.subFranchise, buildShareText("Sub‑Franchise"))
                  : setMsg("Sponsor ID missing. Please re-login.")
              }
            >
              Share Sub‑Franchise
            </Button>
          </Stack>
        )}

        {showMerchantLink && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              sx={{ textTransform: "none" }}
              onClick={() =>
                sponsorId
                  ? shareNative(links.merchant, buildShareText("Merchant"))
                  : setMsg("Sponsor ID missing. Please re-login.")
              }
            >
              Share Merchant
            </Button>
          </Stack>
        )}
      </Stack>

      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {sponsorId ? `Sponsor ID: ${sponsorId}` : "Sponsor ID not available"}
        </Typography>
      </Box>
    </Paper>
  );
}

